import { Button, LabeledValue } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useId } from "react";
import type {
  PlanInformation,
  SingleCoverageInformation,
} from "../../../../data-model/coverageInformation";
import { CoveragePlanDisplayStatus } from "../../../../enums/coverageInformationEnums";
import { ManualEvEvFieldKey } from "../../../../enums/eventsData";
import { formatDisplayDate } from "../../../../utils/formatDate";
import type { PlanActivityStatus } from "../../helpers/coverageStatusUtils";
import type { ManualEvFieldEditedDetail } from "../../helpers/evCoverageAnalyticsEvents";
import {
  EvCoverageEditFieldId,
  evCoverageFieldShowsRequired,
  type EvCoverageFieldId,
} from "../../helpers/evCoverageEditValidation";
import {
  EvCoverageValueWithCopy,
  isEvCoverageValueCopyable,
} from "../evCoverageCopyValueButton";

const planSummaryFieldLabelClassName =
  "!text-tiny !font-normal !leading-tiny !text-primaryGray-6";

const planSummaryFieldValueClassName =
  "!text-smaller !font-semibold !leading-h11 !text-primaryGray-1";

/**
 * Normalizes stored plan dates to `YYYY-MM-DD` for native date inputs.
 *
 * @param iso - ISO or calendar date from EV
 * @returns Empty string when missing/invalid
 */
const toDateInputValue = (iso: string): string => {
  if (!iso) return "";
  const m = moment(iso, ["YYYY-MM-DD", moment.ISO_8601], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export interface EvCoveragePlanSummaryCardProps {
  singleCoverage: SingleCoverageInformation;
  planStatusVariant: PlanActivityStatus;
  /** When true, shows Edit or Cancel/Save and allows plan fields to edit. */
  enablePlanSummaryEdit?: boolean;
  /** When omitted, plan fields stay read-only and action buttons are hidden. */
  isEditing?: boolean;
  onBeginEdit?: () => void;
  /** Discards unsaved plan/financial edits and exits edit mode. */
  onCancelEdit?: () => void;
  onSaveEdit?: () => void | Promise<void>;
  onPlanInformationChange?: (patch: Partial<PlanInformation>) => void;
  /** Debounced `manual_ev_field_edited` while plan-summary fields change in edit mode. */
  onManualEvFieldEdited?: (detail: ManualEvFieldEditedDetail) => void;
  /** Disables Save while the Postgres coverage update is in flight. */
  isSaving?: boolean;
  /** When true, empty editable fields show Oasis `required` styling after Save. */
  showFieldErrors?: boolean;
  /** Field ids that failed validation on the last save attempt. */
  invalidFieldIds?: ReadonlySet<EvCoverageFieldId>;
}

const noop = () => {};

/**
 * EV plan summary strip: plan name (with status indicator) and member id; editable plan name,
 * member id, status, and effective/end dates when `isEditing` is true.
 * In edit mode, plan name and status radios stay grouped on the left; member id and dates stay
 * on the right, with flexible space between.
 * The auth managing entity sits left of member id and is read-only (EV-parsed from the 271);
 * it is omitted entirely when EV returned no recognised entity.
 */
export const EvCoveragePlanSummaryCard = (
  props: EvCoveragePlanSummaryCardProps,
) => {
  const planStatusRadioName = useId();
  const {
    enablePlanSummaryEdit = false,
    isEditing = false,
    onBeginEdit = noop,
    onCancelEdit = noop,
    onSaveEdit = noop,
    onPlanInformationChange = noop,
    onManualEvFieldEdited = noop,
    isSaving = false,
    showFieldErrors = false,
    invalidFieldIds = new Set<EvCoverageFieldId>(),
  } = props;
  const { planInformation } = props.singleCoverage;

  const fieldRequired = (fieldId: EvCoverageFieldId) =>
    evCoverageFieldShowsRequired(showFieldErrors, invalidFieldIds, fieldId);

  const resolvedStatusVariant: PlanActivityStatus =
    planInformation.planStatus === CoveragePlanDisplayStatus.ACTIVE
      ? "active"
      : planInformation.planStatus === CoveragePlanDisplayStatus.INACTIVE
        ? "inactive"
        : props.planStatusVariant;

  const statusDot =
    resolvedStatusVariant === "active" ? (
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-[#008F32]"
        aria-hidden
      />
    ) : resolvedStatusVariant === "inactive" ? (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
        Insurance Inactive
      </span>
    ) : (
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-primaryGray-15"
        aria-hidden
      />
    );

  const planStatusColorDotClass =
    planInformation.planStatus === CoveragePlanDisplayStatus.ACTIVE
      ? "bg-[#008F32]"
      : planInformation.planStatus === CoveragePlanDisplayStatus.INACTIVE
        ? "bg-red-500"
        : "bg-primaryGray-15";

  const planStatusRequired = fieldRequired(EvCoverageEditFieldId.PLAN_STATUS);

  const planStatusRadioGroup = (
    <div
      className="flex shrink-0 flex-col gap-1.5"
      role="group"
      aria-label="Plan status"
    >
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 shrink-0 rounded-full ${planStatusColorDotClass}`}
          aria-hidden
        />
        <span className="text-tiny font-normal leading-tiny text-primaryGray-6">
          Status
          {planStatusRequired ? (
            <span className="text-[#cd0202]" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-smaller font-normal leading-h11 text-primaryGray-1">
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="radio"
            name={planStatusRadioName}
            className="h-3.5 w-3.5 shrink-0 accent-[#0056D6]"
            checked={
              planInformation.planStatus === CoveragePlanDisplayStatus.ACTIVE
            }
            onChange={() => {
              onPlanInformationChange({
                planStatus: CoveragePlanDisplayStatus.ACTIVE,
              });
              onManualEvFieldEdited({
                fieldCategory: "ev",
                fieldKey: ManualEvEvFieldKey.PLAN_STATUS,
                valueEntered: CoveragePlanDisplayStatus.ACTIVE,
              });
            }}
          />
          Active
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="radio"
            name={planStatusRadioName}
            className="h-3.5 w-3.5 shrink-0 accent-[#0056D6]"
            checked={
              planInformation.planStatus === CoveragePlanDisplayStatus.INACTIVE
            }
            onChange={() => {
              onPlanInformationChange({
                planStatus: CoveragePlanDisplayStatus.INACTIVE,
              });
              onManualEvFieldEdited({
                fieldCategory: "ev",
                fieldKey: ManualEvEvFieldKey.PLAN_STATUS,
                valueEntered: CoveragePlanDisplayStatus.INACTIVE,
              });
            }}
          />
          Inactive
        </label>
      </div>
    </div>
  );

  const planNameReadOnly = (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
      <span className="break-words">{planInformation.planName || "—"}</span>
      {statusDot}
    </span>
  );

  const memberIdReadOnly = isEvCoverageValueCopyable(
    planInformation.memberId ?? "",
  ) ? (
    <EvCoverageValueWithCopy
      label="Member ID"
      value={planInformation.memberId}
    />
  ) : (
    planInformation.memberId || "—"
  );

  return (
    <div className="flex flex-shrink-0 flex-col items-start gap-2 self-stretch rounded-md border border-primaryGray-15 px-2.5 pb-px pt-2.5 sm:gap-3 sm:px-3 sm:pt-3 md:gap-[1.125rem] md:px-[1.3125rem] md:pt-[1.3125rem]">
      <div className="flex flex-wrap items-center justify-between gap-2 self-stretch sm:gap-3 md:gap-5">
        <div className="text-h12 font-bold leading-h11 text-primaryGray-1">
          Plan Summary
        </div>
        {enablePlanSummaryEdit && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  buttonType="secondary"
                  size="small"
                  disabled={isSaving}
                  onClick={onCancelEdit}
                  className="!min-w-0"
                  children="Cancel"
                />
                <Button
                  buttonType="primary"
                  size="small"
                  disabled={isSaving}
                  onClick={() => void onSaveEdit()}
                  className="!min-w-0"
                  children={isSaving ? "Saving…" : "Save"}
                />
              </>
            ) : (
              <Button
                buttonType="secondary"
                size="small"
                disabled={false}
                onClick={onBeginEdit}
                className="!min-w-0"
                children="Edit"
              />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2 self-stretch pb-2 sm:gap-x-6 sm:gap-y-3 sm:pb-3 md:gap-x-10 md:pb-4 lg:gap-x-12 lg:pb-5">
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <div className="flex w-full min-w-0 flex-wrap items-end gap-3 sm:gap-4">
              <div className="min-w-0 max-w-md sm:max-w-lg lg:max-w-2xl">
                <LabeledValue
                  label="Plan Name"
                  value={planInformation.planName ?? ""}
                  direction="vertical"
                  gap={4}
                  labelClassName={planSummaryFieldLabelClassName}
                  valueClassName={planSummaryFieldValueClassName}
                  isEditable
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.PLAN_NAME)}
                  onValueChange={(next) => {
                    onPlanInformationChange({ planName: next });
                    onManualEvFieldEdited({
                      fieldCategory: "ev",
                      fieldKey: ManualEvEvFieldKey.PLAN_NAME,
                      valueEntered: next,
                    });
                  }}
                />
              </div>
              <div className="flex shrink-0 items-end">
                {planStatusRadioGroup}
              </div>
            </div>
          ) : (
            <LabeledValue
              label="Plan Name"
              value={planNameReadOnly}
              direction="vertical"
              gap={4}
              labelClassName={planSummaryFieldLabelClassName}
              valueClassName={planSummaryFieldValueClassName}
            />
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-x-3 gap-y-2 sm:gap-x-6 md:gap-x-10 lg:gap-x-12">
          {planInformation.authManagingEntity ? (
            <LabeledValue
              label="Auth Managing Entity"
              value={planInformation.authManagingEntity}
              direction="vertical"
              gap={4}
              labelClassName={planSummaryFieldLabelClassName}
              valueClassName={planSummaryFieldValueClassName}
            />
          ) : null}
          <LabeledValue
            label="Member ID"
            value={
              isEditing ? (planInformation.memberId ?? "") : memberIdReadOnly
            }
            direction="vertical"
            gap={4}
            labelClassName={planSummaryFieldLabelClassName}
            valueClassName={planSummaryFieldValueClassName}
            isEditable={isEditing}
            editAs="text"
            required={fieldRequired(EvCoverageEditFieldId.MEMBER_ID)}
            onValueChange={(next) => {
              onPlanInformationChange({ memberId: next });
              onManualEvFieldEdited({
                fieldCategory: "ev",
                fieldKey: ManualEvEvFieldKey.MEMBER_ID,
                valueEntered: next,
              });
            }}
          />
          <LabeledValue
            label="Effective Date"
            value={
              isEditing
                ? toDateInputValue(planInformation.effectiveDate)
                : formatDisplayDate(planInformation.effectiveDate)
            }
            direction="vertical"
            gap={4}
            labelClassName={planSummaryFieldLabelClassName}
            valueClassName={planSummaryFieldValueClassName}
            isEditable={isEditing}
            editAs="date"
            required={fieldRequired(EvCoverageEditFieldId.EFFECTIVE_DATE)}
            onValueChange={(next) => {
              onPlanInformationChange({ effectiveDate: next });
              onManualEvFieldEdited({
                fieldCategory: "ev",
                fieldKey: ManualEvEvFieldKey.EFFECTIVE_DATE,
                valueEntered: next,
              });
            }}
          />
          <LabeledValue
            label="Plan End Date"
            value={
              isEditing
                ? toDateInputValue(planInformation.planEndDate)
                : formatDisplayDate(planInformation.planEndDate)
            }
            direction="vertical"
            gap={4}
            labelClassName={planSummaryFieldLabelClassName}
            valueClassName={planSummaryFieldValueClassName}
            isEditable={isEditing}
            editAs="date"
            required={fieldRequired(EvCoverageEditFieldId.PLAN_END_DATE)}
            onValueChange={(next) => {
              onPlanInformationChange({ planEndDate: next });
              onManualEvFieldEdited({
                fieldCategory: "ev",
                fieldKey: ManualEvEvFieldKey.PLAN_END_DATE,
                valueEntered: next,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EvCoveragePlanSummaryCard;
